import Promise from 'bluebird';
import _ from 'lodash';
import moment from 'moment';
import worldMap from '../core/map';
import { pinBuilder } from '../core/map/pinBuilder';
import ColorBrewer from '../core/tools/colorbrewer';
import environment from '../environment';
import templates from '../core/templates';
import translate from '../core/tools/translate';
import { POINT_TYPE_COLORS } from './NewGreenfieldLayer';

export const GDSI_COLORS = {
    implemented: '#8F7D6A',
    partially_implemented: '#BBB7AE',
    previous_standard: '#00AAD2'
};

import HaloLayer from './HaloLayer';
import Leaflet from './Leaflet';

const LayerControls = (name) => require('../controls').get(name);
const get = (name, currentMap = worldMap.getCurrent()) => {
    return require('./index').default.get(name, currentMap);
};

export const BROWNFIELD_Z_INDEX_OFFSET = -50000;

const transitionColor = '#8E5AC8';

const linkColor = '#5E6AD2';

class BrownfieldLayer {
    constructor (currentMap) {
        const mapId = +currentMap._id;

        const links = L.layerGroup();

        const clusterImage = new Image();
        clusterImage.src = `${environment.get('IMAGES_HOST')}/icon_marker_greenfield.png`;

        const onClick = (feature, layer) => {
            LayerControls('Brownfield').highlight(layer);
            LayerControls('Brownfield').show({
                show: true,
                id: feature.properties.Id
            });

            return new HaloLayer(currentMap, layer, feature, 'Brownfield', 'points');
        };

        const onSuccess = (res) => {
            const shapeLayer = get('BrownfieldArea', currentMap);
            if (!shapeLayer.visible()) {
                shapeLayer.redraw();
            }

            return new Promise((resolve) => {
                _.forEach(res.features, f => {
                    const { Action, CurrentPointType, PreviousPointType, FuturePointType, ActionStatus } = f.properties;
                    const validType = CurrentPointType || FuturePointType;
                    const hasTransition = PreviousPointType && FuturePointType && PreviousPointType.Id !== FuturePointType.Id;

                    f.properties['Icon'] = 'action';
                    f.properties['ActionName'] = (Action || {}).Name;
                    f.properties['ActionIcon'] = Action ? String(Action.Name).toUnderscoreCase() : 'keep';
                    f.properties['PinIcon'] = `${environment.get('IMAGES_HOST')}/pin_pi_${f.properties.ActionIcon}_xs.png`;
                    f.properties['IdPointType'] = validType ? validType.Id : null;
                    const isTransitionYear = hasTransition && f.properties.YearReference === f.properties.YearAction;
                    const shownType = hasTransition
                        ? (f.properties.YearReference < f.properties.YearAction ? PreviousPointType : FuturePointType)
                        : validType;

                    f.properties['PointTypeTag'] = isTransitionYear
                        ? `${PreviousPointType.Name} → ${FuturePointType.Name}`
                        : (shownType ? shownType.Name : '');
                    f.properties['PointTypeColor'] = isTransitionYear ? transitionColor : POINT_TYPE_COLORS[(shownType || {}).Name];
                    f.properties['CurrentTypeText'] = CurrentPointType ? `${CurrentPointType.Name} — ${CurrentPointType.Description}` : '';
                    f.properties['RecommendedTypeText'] = FuturePointType ? `${FuturePointType.Name} — ${FuturePointType.Description}` : '';
                    f.properties['GdsiKey'] = ActionStatus ? String(ActionStatus.Description).toUnderscoreCase() : '';
                    f.properties['GdsiTag'] = ActionStatus ? translate(`Brownfield.Gdsi.${f.properties.GdsiKey}`) : '';
                    f.properties['GdsiColor'] = GDSI_COLORS[f.properties.GdsiKey];
                    f.properties['ConversionText'] = isTransitionYear
                        ? translate('Brownfield.ConversionValue', { from: PreviousPointType.Name, to: FuturePointType.Name, year: f.properties.YearAction })
                        : '';
                });

                LayerControls('Brownfield').stackFeatures(res.features, currentMap);
                resolve(res);
            });
        };

        const createPopupTemplate = (properties) =>
            new Promise((resolve) => resolve($(templates('map-layer-brownfield-popup', properties))));

        const drawLinks = () => {
            links.clearLayers();

            const markers = get('Brownfield', currentMap).layer.getLayers().filter(l => l.feature);
            const markersByPoint = _.keyBy(markers, m => m.feature.properties.IdPoint);

            const pairs = _(markers)
                .map(origin => [origin, markersByPoint[origin.feature.properties.IdDestinationPoint]])
                .filter(([, destination]) => destination)
                .map(([origin, destination]) => _.endsWith(origin.feature.properties.ActionIcon, '_open') || _.endsWith(destination.feature.properties.ActionIcon, '_close')
                    ? [destination, origin]
                    : [origin, destination])
                .uniqBy(pair => _.sortBy(_.map(pair, m => m.feature.properties.IdPoint)).join('|'))
                .value();

            _.forEach(pairs, ([origin, destination]) => {
                const from = origin.getLatLng();
                const to = destination.getLatLng();
                const fromPoint = currentMap.latLngToLayerPoint(from);
                const toPoint = currentMap.latLngToLayerPoint(to);
                const angle = Math.atan2(toPoint.y - fromPoint.y, toPoint.x - fromPoint.x) * 180 / Math.PI;

                links.addLayer(L.polyline([from, to], {
                    color: linkColor,
                    weight: 3,
                    dashArray: '8 6',
                    interactive: false
                }));

                links.addLayer(L.marker(L.latLng(from.lat + (to.lat - from.lat) * 0.9, from.lng + (to.lng - from.lng) * 0.9), {
                    icon: L.divIcon({
                        className: 'brownfield-link-arrow',
                        html: `<div style="transform: rotate(${angle}deg)">&#10148;</div>`,
                        iconSize: [18, 18],
                        iconAnchor: [9, 9]
                    }),
                    interactive: false,
                    zIndexOffset: BROWNFIELD_Z_INDEX_OFFSET
                }));
            });

            if (!currentMap.hasLayer(links)) {
                links.addTo(currentMap);
            }
        };

        const onComplete = () => {
            drawLinks();

            try {
                if (LayerControls('Voronoi').getControls(mapId).switch.onOff.status) { LayerControls('Voronoi').update(mapId); }
            } catch (error) {
                console.log('Layer not avaliable');
            }

            try {
                LayerControls('NewGreenfield').stack(currentMap);
            } catch (error) {
                console.log('Greenfield Layer not available');
            }

            return LayerControls('Brownfield').show({
                show: false,
                refresh: true
            });
        };

        const onRemove = () => {
            links.clearLayers();

            try {
                if (LayerControls('Voronoi').getControls(mapId).switch.onOff.status) { return LayerControls('Voronoi').update(mapId); }

                const shapeLayer = get('BrownfieldArea', currentMap);
                if (shapeLayer.visible) {
                    shapeLayer.hide();
                }
            } catch (error) {
                console.log('Layer not avaliable');
            }

            try {
                LayerControls('NewGreenfield').stack(currentMap);
            } catch (error) {
                console.log('Greenfield Layer not available');
            }
        };

        const onMoveend = () =>
            LayerControls('Brownfield').show({ refresh: true }, get('Brownfield', currentMap).getCurrentFeatures());

        const clusterTemplate = (cluster) => {
            const data = { value: cluster.getChildCount() };

            return pinBuilder.create.pin.imageWithValue(data, clusterImage);
        };

        return new Leaflet.GeoJSONLayer({
            map: currentMap,
            remote: {
                method: 'GET',
                url: `${environment.get('API_HOST')}/brownfield`,
                params: {
                    year: _.includes(environment.get('CONFIG.FILTERS.BROWNFIELD_SCENARIO_YEARS'), moment().format('YYYY'))
                        ? moment().format('YYYY')
                        : _.first(environment.get('CONFIG.FILTERS.BROWNFIELD_SCENARIO_YEARS'))
                }
            },
            scaleRange: [5, 20],
            filter: {
                location: true
            },
            showAll: true,
            autofocus: false,
            uniqueField: 'Id',
            sort: 'IdAction',
            click: onClick,
            popup: {
                template: createPopupTemplate,
                trigger: 'mouseover',
                options: {
                    offset: L.point(0, -50),
                    closeButton: false,
                    autoclose: true,
                    className: 'leaflet-popup-greenfield'
                },
                single: true
            },
            remove: onRemove,
            dragend: onMoveend,
            zoomend: onMoveend,
            done: onComplete,
            success: onSuccess,
            legend: {
                title: 'Brownfield',
                subtitle: environment.get('CONFIG.FILTERS.BROWNFIELD_LAST_UPDATE')
                    ? translate('Greenfield.UpdatedAt', {
                        date: _.capitalize(moment(environment.get('CONFIG.FILTERS.BROWNFIELD_LAST_UPDATE')).format(translate('Greenfield.UpdatedAtFormat')))
                    })
                    : '',
                text: '{ActionName}',
                description: properties => translate(`Brownfield.ActionDescription.${properties.ActionIcon}`, { empty: true }),
                order: properties => properties.IdAction,
                icon: properties => `${environment.get('IMAGES_HOST')}/pin_pi_${properties.ActionIcon}_xs.png`,
            },
            type: 'cluster',
            symbology: {
                type: 'polymorph',
                cluster: {
                    template: clusterTemplate
                },
                heat: {
                    theme: ColorBrewer.getHeatColors('Oranges')
                },
                property: 'Icon',
                values: [{
                    value: 'action',
                    style: {
                        icon: {
                            fields: ['ActionIcon'],
                            iconUrl: `${environment.get('IMAGES_HOST')}/pin_pi_{ActionIcon}.png`,
                            iconSize: [45, 58], // size of the icon
                            iconAnchor: [22, 55], // point of the icon which will correspond to marker's location
                            labelAnchor: [22, -29]
                        },
                        zIndexOffset: BROWNFIELD_Z_INDEX_OFFSET,
                        riseOnHover: true
                    }
                }, {
                    value: 'action_stacked',
                    style: {
                        icon: {
                            fields: ['ActionIcon'],
                            iconUrl: `${environment.get('IMAGES_HOST')}/pin_pi_{ActionIcon}_xs.png`,
                            iconSize: [26, 26],
                            iconAnchor: [13, -6],
                            labelAnchor: [6, 0],
                            stacked: true
                        },
                        zIndexOffset: BROWNFIELD_Z_INDEX_OFFSET,
                        riseOnHover: true
                    }
                }]
            }
        });
    }
}

export default BrownfieldLayer;



// WEBPACK FOOTER //
// ./src/scripts/layers/BrownfieldLayer.js